import { useState, useEffect, ReactNode, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { RosterStudent, DAYS_SHORT } from '../api';

/**
 * Tooltip del alumno en la lista de la clase (pedido de Carlos 30-jul: «como el de la agenda de
 * la dra. Jesara… me ayudaría ver cuántas clases tendrían y hasta la mensualidad, en vez de ir
 * mes por mes»). Al pasar el cursor: plan, mensualidad, todas sus clases con días y hora, y lo
 * que debe hoy.
 *
 * Propio, sin librería (react-tooltip no está en este proyecto y el bundle ya pesa 810 KB) y en
 * un portal, para que no lo recorte el overflow de la tarjeta ni de la galería de fotos.
 * OJO: se posiciona con el CURSOR, no con el rectángulo del contenedor — el wrapper usa
 * `display:contents` para no romper el grid/flex del padre, y eso no genera caja que medir.
 */
const MES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const mesCorto = (ym: string) => {
  const [a, m] = ym.split('-');
  return `${MES[Number(m) - 1] || m} ${a}`;
};

export default function StudentTooltip({ alumno, children, off = false }: { alumno: RosterStudent; children: ReactNode; off?: boolean }) {
  const [pos, setPos] = useState<{ x: number; y: number; arriba: boolean } | null>(null);

  // off = hay un menú abierto en la lista: el tooltip estorba y tapa las opciones.
  useEffect(() => { if (off) setPos(null); }, [off]);

  const seguir = (e: MouseEvent) => {
    if (off) { setPos(null); return; }
    // Zonas con tooltip propio (los chips de meses): ahí manda el suyo, este se calla.
    if ((e.target as HTMLElement)?.closest?.('[data-no-tip]')) { setPos(null); return; }
    const ANCHO = 280, ALTO = 190;
    const arriba = e.clientY + ALTO + 24 > window.innerHeight;   // no cabe abajo → sale encima
    setPos({
      x: Math.max(8, Math.min(e.clientX + 18, window.innerWidth - ANCHO - 8)),
      y: arriba ? e.clientY - 12 : e.clientY + 18,
      arriba,
    });
  };

  const clases = alumno.clases || [];
  const debe = Number(alumno.debe_total || 0);
  const precio = alumno.effective_month;

  return (
    <>
      <div onMouseEnter={seguir} onMouseMove={seguir} onMouseLeave={() => setPos(null)} className="contents">
        {children}
      </div>
      {pos && createPortal(
        <div
          role="tooltip"
          style={{ left: pos.x, top: pos.y, transform: pos.arriba ? 'translateY(-100%)' : undefined }}
          className="pointer-events-none fixed z-[999] w-[280px] rounded-xl bg-slate-800 px-3 py-2.5 text-[12.5px] leading-snug text-white shadow-xl ring-1 ring-black/20"
        >
          <div className="mb-1 font-semibold">{alumno.first_name} {alumno.last_name}</div>
          <div className="text-slate-300">
            {alumno.plan_name || 'Sin plan'}
            {precio ? ` · $${Number(precio).toLocaleString('es-MX')}/mes` : ' · sin mensualidad'}
          </div>

          <div className="mt-2 border-t border-white/10 pt-1.5">
            <div className="mb-0.5 text-slate-300">
              {clases.length === 0 ? 'Sin clases activas'
                : `${clases.length} ${clases.length === 1 ? 'clase inscrita' : 'clases inscritas'}`}
            </div>
            {clases.slice(0, 5).map((c) => (
              <div key={c.class_id} className="mt-0.5">
                <div className="truncate">· {c.name}</div>
                <div className="pl-2 text-[11.5px] text-slate-400">
                  {c.dias.map((d) => DAYS_SHORT[d]).join('·')}{c.hora ? ` · ${c.hora}` : ''}
                </div>
              </div>
            ))}
            {clases.length > 5 && <div className="mt-0.5 text-slate-400">y {clases.length - 5} más…</div>}
          </div>

          <div className="mt-2 border-t border-white/10 pt-1.5">
            {debe > 0
              ? <span className="font-semibold text-red-300">Debe hoy: ${debe.toLocaleString('es-MX')}
                  {alumno.debe_meses?.length
                    ? <span className="font-normal text-slate-400"> ({alumno.debe_meses.map(mesCorto).join(', ')})</span>
                    : null}
                </span>
              : <span className="font-semibold text-emerald-300">Al corriente</span>}
          </div>
        </div>, document.body)}
    </>
  );
}
